import axios from "axios";
import type { FacilityInstalledUnityGames } from "Models";
import type { UseDataInfo } from "QueryTypes";
import { useSelector } from "react-redux";
import { type RunMutation, useMutation, useRequest } from "redux-query-react";
import type {
  EntitiesState,
  RootState,
  SimulatorsEntitiesState,
} from "StoreTypes";
import type { SimulatorDto } from "Models";
import {
  facilityInstalledUnityGamesQuery,
  updateFacilitySimulatorNameQuery,
  updateFacilitySimulatorsOrderQuery,
} from "./simulatorQueries";
import { getSimulatorsByFacilityId } from "./simulatorSelectors";
import type { QueryState } from "redux-query";
import { clubhouseApiHeadersV1 } from "utils/api/apiHelper";
import { config } from "@/config/env";
const urls = {
  sendMessage(simulatorId?: string): string {
    return `${config.apiBaseUrl}/api/simulators/${simulatorId ? simulatorId + "/" : ""}SendMessage`;
  },
  simCommand(command: string, simulatorId?: string | null): string {
    return `${config.apiBaseUrl}/api/simulators/${simulatorId ? simulatorId + "/" : ""}${command || ""}`;
  },
  timerCommand(
    command: string,
    simulatorId?: string | null,
    value?: string | null,
  ): string {
    return `${config.apiBaseUrl}/api/simulators/${simulatorId ? simulatorId + "/" : ""}timer?command=${command}${value ? "&value=" : ""}${value || ""}`;
  },
  resync(facilityId: string, simulatorId: string): string {
    return `${config.apiBaseUrl}/api/simulators/facility/${facilityId}/simulator/${simulatorId}/resync`;
  },
};

/**
 * Every simulator in the normalised store, keyed by id.
 *
 * For callers that need bays across more than one facility at a time — the
 * per-facility hook can't be called in a loop — and so must group them
 * themselves.
 */
export const useAllSimulators = (): SimulatorsEntitiesState =>
  useSelector((state: RootState) => state.entities.simulators);

/** Simulators belonging to a given facility, from the normalised store. */
export const useSimulatorsByFacility = (facilityId: string): SimulatorDto[] =>
  useSelector((state: RootState) =>
    getSimulatorsByFacilityId(state, facilityId),
  );

export const simulatorControlType = {
  exit: "exit",
  end: "endround",
  shutdown: "SHUTDOWN",
  restart: "RESTART",
};

export const useSendMessageToSimulator = async (
  message: string,
  simulatorId?: string,
  simulatorIds?: string[],
) => {
  await axios
    .post(
      urls.sendMessage(simulatorId),
      { simulatorIds, message },
      clubhouseApiHeadersV1(),
    )
    .then((res) => {
      return res;
    })
    .catch((error) => {
      return error;
    });
};

export const sendControlToSimulator = async (
  command: string,
  simulatorId?: string,
  simulatorIds?: string[],
) => {
  const endpoint = urls.simCommand(command, simulatorId);

  await axios
    .post(endpoint, { simulatorIds }, clubhouseApiHeadersV1())
    .then((res) => {
      return res;
    })
    .catch((error) => {
      return error;
    });
};

export const sendTimerToSimulator = async (
  command: string,
  simulatorId?: string,
  value?: string,
  simulatorIds?: string[],
) => {
  const endpoint = urls.timerCommand(
    command,
    simulatorId || null,
    value || null,
  );

  await axios
    .post(endpoint, { simulatorIds }, clubhouseApiHeadersV1())
    .then((res) => {
      return res;
    })
    .catch((error) => {
      return error;
    });
};

export const sendResyncToSimulator = async (
  facilityId: string,
  simulatorId: string,
) => {
  const endpoint = urls.resync(facilityId, simulatorId);

  await axios
    .patch(endpoint, {}, clubhouseApiHeadersV1())
    .then((res) => {
      return res;
    })
    .catch((error) => {
      throw error;
    });
};

export const useInstalledFacilityUnityGames = (
  facilityId: string,
): UseDataInfo<FacilityInstalledUnityGames> => {
  const [queryState, refreshUnityGames] = useRequest(
    facilityInstalledUnityGamesQuery(facilityId),
  );

  const installedUnityGames = useSelector(
    (state: RootState): FacilityInstalledUnityGames => {
      const installedUnityGames =
        state.entities?.installedUnityGames?.byFacilityId?.[facilityId];
      return installedUnityGames;
    },
  );

  return [installedUnityGames, queryState, refreshUnityGames];
};

export const useInstalledSimulatorUnityGames = (
  facilityId: string,
  simulatorId: string,
) => {
  const [facilityUnityGames] = useInstalledFacilityUnityGames(facilityId);

  const installedSimulatorUnityGames =
    facilityUnityGames?.simulators.find(
      (simulator) => simulator?.simulatorId === simulatorId,
    )?.games || [];

  return installedSimulatorUnityGames;
};

export const useFacilitySimulatorsPatch = (
  facilityId: string,
): [
  QueryState,
  RunMutation<
    EntitiesState,
    [
      {
        id: string;
        name: string;
      }[],
    ]
  >,
] => {
  const [updateFacilitySimulatorsQueryState, updateFacilitySimulators] =
    useMutation((body: { id: string; name: string }[]) =>
      updateFacilitySimulatorsOrderQuery(facilityId, body),
    );

  return [updateFacilitySimulatorsQueryState, updateFacilitySimulators];
};

export const useFacilitySimulatorNamePatch = (
  facilityId: string,
): [
  QueryState,
  RunMutation<
    EntitiesState,
    [
      {
        id: string;
        name: string;
      }[],
    ]
  >,
] => {
  const [updateFacilitySimulatorNameQueryState, updateFacilitySimulatorName] =
    useMutation((body: { id: string; name: string }[]) =>
      updateFacilitySimulatorNameQuery(facilityId, body),
    );

  return [updateFacilitySimulatorNameQueryState, updateFacilitySimulatorName];
};
